import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import customerManufacturersApi from "../shared/api/customerManufacturersApi";
import customerCategoriesApi from "../shared/api/customerCategoriesApi";
import { ChangeOffer, CustomerSentOffersState, OffersQuery, OffersQueryParameter } from "./models/models";
import customerSupplierOffersApi from "./api/customerSupplierOffersApi";
import publicRfqsApi from "../rfqFeed/api/publicRfqsApi";

export const getOffers = createAsyncThunk(
  "customerSentOffers/getOffers",
  async (query: OffersQuery) => {
    return customerSupplierOffersApi.getOffersAsync(query);
  }
);

export const getRfq = createAsyncThunk(
  "customerSentOffers/getRfq",
  async (id: string) => {
    return publicRfqsApi.getRfqAsync(id);
  }
);

export const createOffer = createAsyncThunk(
  "customerSentOffers/createOffer",
  async (model: ChangeOffer) => {
    return customerSupplierOffersApi.createOfferAsync(model);
  }
);

export const cancelOffer = createAsyncThunk(
  "customerSentOffers/cancelOffer",
  async (id: string) => {
    return customerSupplierOffersApi.cancelOffer(id);
  }
);

export const deleteOffer = createAsyncThunk(
  "customerSentOffers/deleteOffer",
  async (id: string) => {
    return customerSupplierOffersApi.deleteOffer(id);
  }
);

export const getManufacturers = createAsyncThunk(
  "customerSentOffers/getManufacturers",
  async () => {
    return customerManufacturersApi.getManufacturers();
  }
);

export const getCategories = createAsyncThunk(
  "customerSentOffers/getCategories",
  async () => {
    return customerCategoriesApi.getCategories();
  }
);

const initialState: CustomerSentOffersState = {
  query: {
    search: undefined,
    statusCode: undefined,
    sortBy: OffersQueryParameter.createdDate,
    sortDesc: true,
    pageNo: 1,
    pageSize: 20
  },
  rfq: undefined,
  offers: undefined,
  offer: undefined,
  manufacturers: [],
  categories: [],
  loading: false,
  success: undefined,
  error: undefined
};

const customerSentOffersSlice = createSlice({
  name: "customerSentOffers",
  initialState: initialState,
  reducers: {
    changeQuery: (state, action) => {
      state.query = action.payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getOffers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOffers.fulfilled, (state, { payload }) => {
        state.offers = payload;
        state.loading = false;
      })
      .addCase(getOffers.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getRfq.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRfq.fulfilled, (state, { payload }) => {
        state.rfq = payload;
        state.loading = false;
      })
      .addCase(getRfq.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(createOffer.pending, (state) => {
        state.loading = true;
      })
      .addCase(createOffer.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(createOffer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getManufacturers.fulfilled, (state, { payload }) => {
        state.manufacturers = payload;
      });

    builder
      .addCase(getCategories.fulfilled, (state, { payload }) => {
        state.categories = payload;
      });
  }
});

export const {
  changeQuery,
  setLoading,
  setSuccess,
  setError
} = customerSentOffersSlice.actions;

export default customerSentOffersSlice.reducer;